/* eslint-disable camelcase */
import {
  CertificateRadio,
  CertificateType,
  CertificateValueBorn,
  CertificateValueCitizenship,
  CertificateValueCivilUnion,
  CertificateValueDeath,
  CertificateValueMarriage,
  CertificationFormVM,
} from 'front-office/view-models/document-birth-form';
import { DateTime } from 'luxon';
import { SubjectData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';

const transformCertificateType = (certificate?: CertificateRadio | null): string => {
  let certificateType = 'ASC';

  const isCopia =
    certificate?.value === CertificateValueBorn.copia ||
    certificate?.value === CertificateValueMarriage.copia ||
    certificate?.value === CertificateValueDeath.copia ||
    certificate?.value === CertificateValueCivilUnion.copia ||
    certificate?.value === CertificateValueCitizenship.copia;

  if (isCopia) {
    certificateType = `${certificateType}COPIACNF`;
    return certificateType;
  }

  const isCertificato =
    certificate?.value === CertificateValueBorn.certificato ||
    certificate?.value === CertificateValueMarriage.certificato ||
    certificate?.value === CertificateValueDeath.certificato ||
    certificate?.value === CertificateValueCivilUnion.certificato ||
    certificate?.value === CertificateValueCitizenship.certificato;

  if (isCertificato) {
    certificateType = `${certificateType}CRT`;
  }

  const isEstratto =
    certificate?.value === CertificateValueBorn.estratto ||
    certificate?.value === CertificateValueMarriage.estratto ||
    certificate?.value === CertificateValueDeath.estratto ||
    certificate?.value === CertificateValueCivilUnion.estratto ||
    certificate?.value === CertificateValueCitizenship.estratto;

  if (isEstratto) {
    certificateType = `${certificateType}ESN`;
  }

  if (certificate?.type === CertificateType.nascita) {
    certificateType = `${certificateType}NASC`;
  }

  if (certificate?.type === CertificateType.morte) {
    certificateType = `${certificateType}MRT`;
  }

  if (certificate?.type === CertificateType.matrimonio) {
    certificateType = `${certificateType}MATR`;
  }

  if (certificate?.type === CertificateType.unioneCivile) {
    certificateType = `${certificateType}UCIV`;
  }

  if (certificate?.type === CertificateType.cittadinanza) {
    certificateType = `${certificateType}CITT`;
  }

  const isEstrattoParigi =
    certificate?.value === CertificateValueBorn.estrattoParigi ||
    certificate?.value === CertificateValueMarriage.estrattoParigi ||
    certificate?.value === CertificateValueDeath.estrattoParigi;

  if (isEstrattoParigi) {
    certificateType = certificateType.replace('ASC', 'ASCESI');
    certificateType = `${certificateType}P`;
  }

  const isEstrattoVienna =
    certificate?.value === CertificateValueBorn.estrattoVienna ||
    certificate?.value === CertificateValueMarriage.estrattoVienna ||
    certificate?.value === CertificateValueDeath.estrattoVienna;

  if (isEstrattoVienna) {
    certificateType = certificateType.replace('ASC', 'ASCESI');
    certificateType = `${certificateType}V`;
  }

  return certificateType;
};

const transformExtract = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const isEstratto =
    values.certificate?.value === CertificateValueBorn.estratto ||
    values.certificate?.value === CertificateValueMarriage.estratto ||
    values.certificate?.value === CertificateValueDeath.estratto ||
    values.certificate?.value === CertificateValueCivilUnion.estratto ||
    values.certificate?.value === CertificateValueCitizenship.estratto;

  const isEstrattoParigi =
    values.certificate?.value === CertificateValueBorn.estrattoParigi ||
    values.certificate?.value === CertificateValueMarriage.estrattoParigi ||
    values.certificate?.value === CertificateValueDeath.estrattoParigi;

  const isEstrattoVienna =
    values.certificate?.value === CertificateValueBorn.estrattoVienna ||
    values.certificate?.value === CertificateValueMarriage.estrattoVienna ||
    values.certificate?.value === CertificateValueDeath.estrattoVienna;

  if (isEstratto || isEstrattoParigi || isEstrattoVienna) {
    let extract: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      cartaLibera: values.flagCarta ? '1' : '0',
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
    };

    if (values.certificate?.type === CertificateType.nascita) {
      extract.nascitaEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'NASCITA',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenBirth?.surname || '',
            nome: values.documentChosenBirth?.name || '',
            sesso: values.documentChosenBirth?.gender || '',
          },
          datiPadre: {
            cognome: values.documentChosenBirth?.surnameFather || '',
            nome: values.documentChosenBirth?.nameFather || '',
          },
          datiMadre: {
            cognome: values.documentChosenBirth?.surnameMother || '',
            nome: values.documentChosenBirth?.nameMother || '',
          },
          definizioneNascita: {
            luogoNascita: values.documentChosenBirth?.municipalityBirth || '',
            dataNascita: values.documentChosenBirth?.birthday?.toFormat('dd/MM/yyyy') || '',
            oraNascita: values.documentChosenBirth?.time?.toFormat('HH:mm') || '',
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoNascitaVO: extract };
    }

    if (values.certificate?.type === CertificateType.morte) {
      extract.morteEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MORTE',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenDeath?.surname || '',
            nome: values.documentChosenDeath?.name || '',
            sesso: values.documentChosenDeath?.gender || '',
            statoCivile: values.documentChosenDeath?.status,
            residenza: values.documentChosenDeath?.residenceMunicipality,
            coniuge: `${values.documentChosenDeath?.spouseSurname || ''} ${
              values.documentChosenDeath?.spouseName || ''
            }`,
            nomeConiuge: values.documentChosenDeath?.spouseName,
            cognomeConiuge: values.documentChosenDeath?.spouseSurname,
            nascita: {
              luogo: values.documentChosenDeath?.municipalityBirth,
              data: values.documentChosenDeath?.birthday?.toFormat('dd/MM/yyyy'),
              eta: values.documentChosenDeath?.years,
              anno: values.documentChosenDeath?.yearDate,
            },
          },
          datiMorte: {
            luogo: values.documentChosenDeath?.deathMunicipality,
            dataMorteCerta:
              values?.documentChosenDeath?.deathPresumed || !values.documentChosenDeath?.deathDate
                ? ''
                : values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy'),
            dataMortePresuntaDal: values.documentChosenDeath?.deathBy
              ? values.documentChosenDeath?.deathBy?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            dataMortePresuntaAl: values.documentChosenDeath?.deathTo
              ? values.documentChosenDeath?.deathTo?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            orarioMorte: values?.documentChosenDeath?.hourDeathUnknown
              ? 'imprecisate'
              : values?.documentChosenDeath?.hourDeathDate
              ? values.documentChosenDeath.hourDeathDate.toFormat('HH:mm')
              : ' ',
          },
          // datiPadre: {
          //   cognome: values.documentChosenDeath?.surnameFather || '',
          //   nome: values.documentChosenDeath?.nameFather || '',
          // },
          // datiMadre: {
          //   cognome: values.documentChosenDeath?.surnameMother || '',
          //   nome: values.documentChosenDeath?.nameMother || '',
          // },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoMorteVO: extract };
    }

    if (values.certificate?.type === CertificateType.matrimonio) {
      extract.matrimonioEstrattoElement = <const>[
        {
          cognomeDopoMatrimonioMarito: values.documentChosenMarriage?.surnameAfterMarriageHusband,
          cognomeDopoMatrimonioMoglie: values.documentChosenMarriage?.surnameAfterMarriageWife,
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MATRIMONIO',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          coniugeUno: {
            cognome: values.documentChosenMarriage?.surnameHusband,
            nome: values.documentChosenMarriage?.nameHusband,
            sesso: values.documentChosenMarriage?.genderHusband,
            cittadinanza: values.documentChosenMarriage?.citizenshipHusband,
            statoCivile: values.documentChosenMarriage?.statusHusband,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthHusband,
              data: values.documentChosenMarriage?.birthdayHusband?.toFormat('dd/MM/yyyy') || '',
              eta: values.documentChosenMarriage?.yearsHusband,
              anno: values.documentChosenMarriage?.yearDateHusband,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityHusband,
          },
          coniugeDue: {
            cognome: values.documentChosenMarriage?.surnameWife,
            nome: values.documentChosenMarriage?.nameWife,
            sesso: values.documentChosenMarriage?.genderWife,
            statoCivile: values.documentChosenMarriage?.statusWife,
            cittadinanza: values.documentChosenMarriage?.citizenshipWife,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthWife,
              data: values.documentChosenMarriage?.birthdayWife?.toFormat('dd/MM/yyyy') || '',
              eta: values.documentChosenMarriage?.yearsWife,
              anno: values.documentChosenMarriage?.yearDateWife,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityWife,
          },
          datiMatrimonio: {
            data: values.documentChosenMarriage?.marriageDate?.toFormat('dd/MM/yyyy'),
            luogo: values.documentChosenMarriage?.marriageMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoMatrimonioVO: extract };
    }

    if (values.certificate?.type === CertificateType.unioneCivile) {
      extract.unioneCivileEstrattoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'UNIONE CIVILE',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year || undefined,
            numero: values.chosenDocument?.documentNumber || undefined,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte || undefined,
            serie: values.chosenDocument?.serie || ' ',
          },
          dichiaranteUno: {
            cognome: values.documentChosenCivilUnion?.surname || undefined,
            nome: values.documentChosenCivilUnion?.name || undefined,
            sesso: values.documentChosenCivilUnion?.gender || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth || undefined,
              data: values.documentChosenCivilUnion?.birthday
                ? values.documentChosenCivilUnion?.birthday.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant1,
              anno: values.documentChosenCivilUnion?.yearsDeclarant1 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant1,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant1,
          },
          dichiaranteDue: {
            cognome: values.documentChosenCivilUnion?.surname2 || undefined,
            nome: values.documentChosenCivilUnion?.name2 || undefined,
            sesso: values.documentChosenCivilUnion?.gender2 || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth2 || undefined,
              data: values.documentChosenCivilUnion?.birthday2
                ? values.documentChosenCivilUnion?.birthday2.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant2,
              anno: values.documentChosenCivilUnion?.yearsDeclarant2 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant2,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant2,
          },
          datiUnione: {
            data: values.documentChosenCivilUnion?.civilUnionDate?.toFormat('dd/MM/yyyy'),
            luogo: values.documentChosenCivilUnion?.civilUnionMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      extract = { stampaEstrattoUnioneCivileVO: extract };
    }

    return extract;
  }

  return {};
};

const transformCertification = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const isCertificato =
    values.certificate?.value === CertificateValueBorn.certificato ||
    values.certificate?.value === CertificateValueMarriage.certificato ||
    values.certificate?.value === CertificateValueDeath.certificato ||
    values.certificate?.value === CertificateValueCivilUnion.certificato ||
    values.certificate?.value === CertificateValueCitizenship.certificato;

  if (isCertificato) {
    let certification: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
      cartaLibera: values.flagCarta ? '1' : '0',
    };

    if (values.certificate?.type === CertificateType.nascita) {
      certification.nascitaCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'NASCITA',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenBirth?.surname || '',
            nome: values.documentChosenBirth?.name || '',
            sesso: values.documentChosenBirth?.gender || '',
          },
          datiPadre: {
            cognome: values.documentChosenBirth?.surnameFather || '',
            nome: values.documentChosenBirth?.nameFather || '',
          },
          datiMadre: {
            cognome: values.documentChosenBirth?.surnameMother || '',
            nome: values.documentChosenBirth?.nameMother || '',
          },
          definizioneNascita: {
            luogoNascita: values.documentChosenBirth?.municipalityBirth || '',
            dataNascita: values.documentChosenBirth?.birthday?.toFormat('dd/MM/yyyy') || '',
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoNascitaVO: certification };
    }

    if (values.certificate?.type === CertificateType.morte) {
      certification.morteCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MORTE',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          datiCittadino: {
            cognome: values.documentChosenDeath?.surname || '',
            nome: values.documentChosenDeath?.name || '',
            sesso: values.documentChosenDeath?.gender || '',
            statoCivile: values.documentChosenDeath?.status,
            residenza: values.documentChosenDeath?.residenceMunicipality,
            coniuge: `${values.documentChosenDeath?.spouseSurname || ''} ${
              values.documentChosenDeath?.spouseName || ''
            }`,
            nascita: {
              luogo: values.documentChosenDeath?.municipalityBirth,
              data: values.documentChosenDeath?.birthday?.toFormat('dd/MM/yyyy'),
              eta: values.documentChosenDeath?.years,
              anno: values.documentChosenDeath?.yearDate,
            },
          },
          datiMorte: {
            luogo: values.documentChosenDeath?.deathMunicipality,
            dataMorteCerta:
              values?.documentChosenDeath?.deathPresumed || !values.documentChosenDeath?.deathDate
                ? ''
                : values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy'),
            dataMortePresuntaDal: values.documentChosenDeath?.deathBy
              ? values.documentChosenDeath?.deathBy?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
            dataMortePresuntaAl: values.documentChosenDeath?.deathTo
              ? values.documentChosenDeath?.deathTo?.toFormat('dd/MM/yyyy')
              : values?.documentChosenDeath?.deathPresumed && values.documentChosenDeath?.deathDate
              ? values.documentChosenDeath?.deathDate?.toFormat('dd/MM/yyyy')
              : '',
          },
          // datiPadre: {
          //   cognome: values.documentChosenDeath?.surnameFather || '',
          //   nome: values.documentChosenDeath?.nameFather || '',
          // },
          // datiMadre: {
          //   cognome: values.documentChosenDeath?.surnameMother || '',
          //   nome: values.documentChosenDeath?.nameMother || '',
          // },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoMorteVO: certification };
    }

    if (values.certificate?.type === CertificateType.matrimonio) {
      certification.matrimonioCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'MATRIMONIO',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year,
            numero: values.chosenDocument?.documentNumber,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte,
            serie: values.chosenDocument?.serie || ' ',
          },
          coniugeUno: {
            cognome: values.documentChosenMarriage?.surnameHusband,
            nome: values.documentChosenMarriage?.nameHusband,
            sesso: values.documentChosenMarriage?.genderHusband,
            statoCivile: values.documentChosenMarriage?.statusHusband,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthHusband,
              data: values.documentChosenMarriage?.birthdayHusband?.toFormat('dd/MM/yyyy') || '',
              eta: values.documentChosenMarriage?.yearsHusband,
              anno: values.documentChosenMarriage?.yearDateHusband,
            },
            cittadinanza: values.documentChosenMarriage?.citizenshipHusband,
            residenza: values.documentChosenMarriage?.residenceMunicipalityHusband,
          },
          coniugeDue: {
            cognome: values.documentChosenMarriage?.surnameWife,
            nome: values.documentChosenMarriage?.nameWife,
            sesso: values.documentChosenMarriage?.genderWife,
            cittadinanza: values.documentChosenMarriage?.citizenshipWife,
            statoCivile: values.documentChosenMarriage?.statusWife,
            nascita: {
              luogo: values.documentChosenMarriage?.municipalityBirthWife,
              data: values.documentChosenMarriage?.birthdayWife?.toFormat('dd/MM/yyyy') || '',
              eta: values.documentChosenMarriage?.yearsWife,
              anno: values.documentChosenMarriage?.yearDateWife,
            },
            residenza: values.documentChosenMarriage?.residenceMunicipalityWife,
          },
          datiMatrimonio: {
            data: values.documentChosenMarriage?.marriageDate?.toFormat('dd/MM/yyyy'),
            luogo: values.documentChosenMarriage?.marriageMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoMatrimonioVO: certification };
    }

    if (values.certificate?.type === CertificateType.unioneCivile) {
      certification.unioneCivileCertificatoElement = <const>[
        {
          cartaBianca: values.flagFincatura || false,
          intestazione: {
            ufficio: 'UFFICIO STATO CIVILE',
            tipoDocumento: 'UNIONE CIVILE',
          },
          definizioneAtto: {
            anno: values.chosenDocument?.year || undefined,
            numero: values.chosenDocument?.documentNumber || undefined,
            registro: values.chosenDocument?.numReg || undefined,
            descrizioneRegistro: registryItem?.value,
            parte: values.chosenDocument?.parte || undefined,
            serie: values.chosenDocument?.serie || ' ',
          },
          dichiaranteUno: {
            cognome: values.documentChosenCivilUnion?.surname || undefined,
            nome: values.documentChosenCivilUnion?.name || undefined,
            sesso: values.documentChosenCivilUnion?.gender || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth || undefined,
              data: values.documentChosenCivilUnion?.birthday
                ? values.documentChosenCivilUnion?.birthday.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant1,
              anno: values.documentChosenCivilUnion?.yearsDeclarant1 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant1,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant1,
          },
          dichiaranteDue: {
            cognome: values.documentChosenCivilUnion?.surname2 || undefined,
            nome: values.documentChosenCivilUnion?.name2 || undefined,
            sesso: values.documentChosenCivilUnion?.gender2 || undefined,
            nascita: {
              luogo: values.documentChosenCivilUnion?.municipalityBirth2 || undefined,
              data: values.documentChosenCivilUnion?.birthday2
                ? values.documentChosenCivilUnion?.birthday2.toFormat('dd/MM/yyyy')
                : undefined,
              eta: values.documentChosenCivilUnion?.yearDateDeclarant2,
              anno: values.documentChosenCivilUnion?.yearsDeclarant2 || undefined,
            },
            residenza: values.documentChosenCivilUnion?.residenceMunicipalityDeclarant2,
            cittadinanza: values.documentChosenCivilUnion?.citizenshipDeclarant2,
          },
          datiUnione: {
            data: values.documentChosenCivilUnion?.civilUnionDate?.toFormat('dd/MM/yyyy'),
            luogo: values.documentChosenCivilUnion?.civilUnionMunicipality,
          },
          timbri: {
            timbroFisso: values.timber || '',
            timbroLibero: values.reason || '',
          },
          pieDiPagina: {
            dataStampa: DateTime.now().toFormat('dd/MM/yyyy'),
            operatore: nomeOperatore,
            // etichettaOperatore
            // tipoRilascio
            // diritti
            // numCertificato
          },
        },
      ];
      certification = { stampaCertificatoUnioneCivileVO: certification };
    }

    return certification;
  }

  return {};
};

const transformCopy = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  registryItem: ItemVM | undefined
) => {
  const isCopia =
    values.certificate?.value === CertificateValueBorn.copia ||
    values.certificate?.value === CertificateValueMarriage.copia ||
    values.certificate?.value === CertificateValueDeath.copia ||
    values.certificate?.value === CertificateValueCivilUnion.copia ||
    values.certificate?.value === CertificateValueCitizenship.copia;

  if (isCopia) {
    const copy: Record<string, unknown> = {
      usoInterno: values.flagInternal ? '1' : '0',
      numeroCopie: values.copies,
      firmaOmessa: values.flagOmittedSign ? '1' : '0',
      cartaLibera: values.flagCarta ? '1' : '0',
    };

    let codici: Array<Record<string, unknown>> = [];

    if (values.certificate?.type === CertificateType.nascita) {
      copy.immagini = (values.documentChosenBirth?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenBirth?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.certificate?.type === CertificateType.morte) {
      copy.immagini = (values.documentChosenDeath?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenDeath?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.certificate?.type === CertificateType.matrimonio) {
      copy.immagini = (values.documentChosenMarriage?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenMarriage?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.certificate?.type === CertificateType.unioneCivile) {
      copy.immagini = (values.documentChosenCivilUnion?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenCivilUnion?.uploadFile?.file?.name,
        },
      ];
    }

    if (values.certificate?.type === CertificateType.cittadinanza) {
      copy.immagini = (values.documentChosenCitizenship?.uploadFile?.raw as Array<string>).map(
        (el) => el.split(',')[1]
      );
      codici = [
        {
          qrcode: 'dummy',
          img_nested_JPG_0_0_base64: values.documentChosenCitizenship?.uploadFile?.file?.name,
        },
      ];
    }

    copy.copiaConformeElement = [
      {
        cartaBianca: values.flagFincatura || false,
        intestazione: '',
        codici,
        definizioneAtto: {
          anno: values.chosenDocument?.year,
          numero: values.chosenDocument?.documentNumber,
          registro: values.chosenDocument?.numReg || undefined,
          descrizioneRegistro: registryItem?.value,
          parte: values.chosenDocument?.parte,
          serie: values.chosenDocument?.serie || ' ',
        },
        timbri: {
          timbroFisso: values.timber || '',
          timbroLibero: values.reason || '',
          uso: values.flagInternal ? '1' : '0',
        },
        pieDiPagina: {
          luogoStampa: '',
          dataStampa: '',
          operatore: nomeOperatore,
          etichettaOperatore: '',
          notaFirma: '',
          tipoRilascio: '',
          diritti: '',
          numCertificato: '',
        },
      },
    ];

    return { stampaCopiaConformeVO: copy };
  }

  return {};
};

const transformUtilsRegistroDocRilasciatiVO = (
  values: CertificationFormVM,
  codiceServizioOperatore: string
): Record<string, unknown> => {
  const hasDatiSoggetto = !!values.datiSoggetto;
  let datiSoggetto: SubjectData | undefined = undefined;

  if (hasDatiSoggetto) {
    let codiceFiscale;

    let comune;

    let localita;

    let cartaIdentita;

    if (values.fiscalCode) {
      codiceFiscale = values.fiscalCode
        ? {
            codFiscale: values.fiscalCode,
          }
        : undefined;
    } else if (values.datiSoggetto?.generalita?.codiceFiscale) {
      codiceFiscale = values.datiSoggetto?.generalita?.codiceFiscale;
    }

    if (!values.isForeign) {
      if (values.municipality?.code) {
        comune = {
          codiceIstat: values.municipality.id || '',
          nomeComune: values.municipality.description,
          siglaProvinciaIstat: values.municipality.province,
        };
      } else if (values.datiSoggetto?.generalita?.luogoNascita?.comune) {
        comune = values.datiSoggetto?.generalita?.luogoNascita?.comune;
      }
    } else {
      if (values.foreignMunicipality?.code && values.foreignState?.code) {
        localita = {
          codiceStato: values.foreignState?.code,
          descrizioneStato: values.foreignState?.value,
          descrizioneLocalita: values.foreignMunicipality?.description,
          provinciaContea: values.foreignState?.province,
        };
      } else if (values.datiSoggetto?.generalita?.luogoNascita?.localita) {
        localita = values.datiSoggetto?.generalita?.luogoNascita?.localita;
      }
    }

    if (values.municipalityDocument?.id || values.documentNumber || values.documentIssuedDate) {
      cartaIdentita = {
        comuneRilascio: values.municipalityDocument?.id
          ? {
              codiceIstat: values.municipalityDocument?.id,
              nomeComune: values.municipalityDocument?.description,
              siglaProvinciaIstat: values.municipalityDocument?.province,
            }
          : undefined,
        numero: values.documentNumber || undefined,
        dataRilascio: values.documentIssuedDate?.toFormat('yyyy-MM-dd') || undefined,
      };
    } else if (values.datiSoggetto?.cartaIdentita) {
      cartaIdentita = values.isForeignDocument
        ? undefined
        : (values.datiSoggetto || {}).cartaIdentita;
    }

    datiSoggetto = {
      ...(values.datiSoggetto || {}),
      generalita: {
        codiceFiscale,
        cognome: values.surname || values.datiSoggetto?.generalita?.cognome,
        nome: values.name || values.datiSoggetto?.generalita?.nome,
        dataNascita:
          values.birthDate?.toFormat('yyyy-MM-dd') || values.datiSoggetto?.generalita?.dataNascita,
        sesso: values.gender || values.datiSoggetto?.generalita?.sesso,
        luogoNascita: {
          comune,
          localita,
        },
      },
      cartaIdentita,
    };
  } else {
    datiSoggetto = {
      generalita: {
        codiceFiscale: values.fiscalCode
          ? {
              codFiscale: values.fiscalCode,
            }
          : undefined,
        cognome: values.surname,
        nome: values.name,
        dataNascita: values.birthDate?.toFormat('yyyy-MM-dd'),
        luogoNascita: {
          comune:
            !values.isForeign && values.municipality?.id
              ? {
                  codiceIstat: values.municipality.id || '',
                  nomeComune: values.municipality.description,
                  siglaProvinciaIstat: values.municipality.province,
                }
              : undefined,
          localita:
            values.isForeign && values.foreignMunicipality?.code && values.foreignState?.id
              ? {
                  codiceStato: values.foreignState?.id,
                  descrizioneStato: values.foreignState?.value,
                  descrizioneLocalita: values.foreignMunicipality?.description,
                  provinciaContea: values.foreignState?.province,
                }
              : undefined,
        },
        sesso: values.gender,
      },
      cartaIdentita:
        values.municipalityDocument?.id || values.documentNumber || values.documentIssuedDate
          ? {
              comuneRilascio: values.municipalityDocument?.id
                ? {
                    codiceIstat: values.municipalityDocument?.id,
                    nomeComune: values.municipalityDocument?.description,
                    siglaProvinciaIstat: values.municipalityDocument?.province,
                  }
                : undefined,
              numero: values.documentNumber || undefined,
              dataRilascio: values.documentIssuedDate?.toFormat('yyyy-MM-dd') || undefined,
            }
          : undefined,
    };
  }

  return {
    codiceFiscaleRichiedente: values.fiscalCode || undefined,
    idAnprRichiedente: values.idSubjectAnpr || undefined,
    codiceModificatore: values.modifierCode,
    codiceServizioOperatore,
    //numeroRicevutaPos, // int TODO in attesa di specifiche
    datiSoggetto: datiSoggetto as SubjectData,
    motivoRilascioRichiedente: values.reasonRelease,
    docRiconoscimentoRichiedente: values.isForeignDocument
      ? {
          estDocRiconoscimento: values.foreignStateDocument || undefined,
          dataRilascio: values.documentIssuedDate
            ? values.documentIssuedDate.toFormat('yyyy-MM-dd')
            : undefined,
        }
      : undefined,
  };
};

export const transformCertificate = (
  values: CertificationFormVM,
  isNotFlagSign: boolean | undefined,
  nomeOperatore: string | undefined,
  codiceServizioOperatore: string,
  registryItem: ItemVM | undefined
): Record<string, unknown> => {
  const tipoStampa = transformCertificateType(values.certificate);

  const extract = transformExtract(values, isNotFlagSign, nomeOperatore, registryItem);

  const certification = transformCertification(values, isNotFlagSign, nomeOperatore, registryItem);

  const copy = transformCopy(values, isNotFlagSign, nomeOperatore, registryItem);

  const utilsRegistroDocRilasciatiVO = transformUtilsRegistroDocRilasciatiVO(
    values,
    codiceServizioOperatore
  );

  return <const>{
    tipoStampa,
    tipoDocumento: values.certificate?.value || undefined,
    tipoPagamento: values.typePayment || undefined,
    utilsRegistroDocRilasciatiVO,
    ...extract,
    ...certification,
    ...copy,
  };
};
